import { Button, Flex, SimpleGrid, Tabs, Text, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import get from "lodash/get";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useIDPResult } from "../../api/hooks/idpFd/useIDPResult";
import { useUpdateIdpFdDocument } from "../../api/hooks/idpFd/useUpdateIdpFdDocument";
import { useTrack } from "../../api/hooks/user/useTrack.ts";
import SvgCheckCircle from "../../components/Icons/CheckCircle.tsx";
import SvgCloseCircle from "../../components/Icons/CloseCircle.tsx";
import SvgDownload from "../../components/Icons/Download.tsx";
import SvgUpload from "../../components/Icons/Upload.tsx";
import Layout from "../../components/Layout/Layout.tsx";
import { Loader } from "../../components/Loader";
import { AppRoute } from "../../constants.ts";
import { ActivityTrackerTypes } from "../../graphql/generated.ts";
import { GetIdpFdQuery } from "../../graphql/generated.ts";
import { Status } from "../../graphql/generated.ts";
import { theme } from "../../utils/mantine/theme.ts";
import DocumentPreview from "./DocumentPreview/DocumentPreview.tsx";
import DocumentUploadConfirmationDialog from "./DocumentUploadConfirmationDialog.tsx";
import DocumentUploadModal from "./DocumentUploadModal.tsx";
import ExtractedDataDisplay from "./ExtractedDataDisplay/ExtractedDataDisplay.tsx";
import ForgeryResultDisplay from "./ForgeryResultDisplay/ForgeryResultDisplay.tsx";
import { useDocumentProcessing } from "./hooks/useDocumentProcessing.tsx";
import ProcessingModal from "./ProcessingModal.tsx";
import {
  DocumentStatus,
  DocumentType,
  ForgeryCheckResult,
  ForgeryCheckType,
  ForgeryResult,
  ForgeryStatus,
  JSONObject,
  JSONValue,
  UploadedDocument,
} from "./types.ts";
import { downloadCSV, downloadExcel } from "./utils.ts";
import { mapDocTypeToDocumentType } from "./utils.ts";
import {
  getCategoryFromMethod,
  isJSONArray,
  isJSONObject,
  isValidJSONValue,
} from "./utils.ts";

const IDPForgeryCheckPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const documentId = searchParams.get("id");
  const [, track] = useTrack();

  const [uploadModalOpened, setUploadModalOpened] = useState(false);
  const [confirmationDialogOpened, setConfirmationDialogOpened] =
    useState(false);
  const [processingModalOpened, setProcessingModalOpened] = useState(false);
  const [pendingUpload, setPendingUpload] = useState<{
    file: File;
    documentType: DocumentType;
    customerId: number;
  } | null>(null);

  const [activeTab, setActiveTab] = useState<string>("extractedData");

  const [
    { data: idpData, fetching: fetchingIdpData, error: idpError },
    reexecuteIdpQuery,
  ] = useIDPResult({
    id: documentId ? Number(documentId) : 0,
  });

  const { updateIdpResult, loading: updateLoading } = useUpdateIdpFdDocument();

  const { processDocument, loading: loadingSubmitDocument } =
    useDocumentProcessing();

  const [currentDocument, setCurrentDocument] =
    useState<UploadedDocument | null>(null);
  const [currentExtractedData, setCurrentExtractedData] =
    useState<JSONValue | null>(null);
  const [currentForgeryResult, setCurrentForgeryResult] =
    useState<ForgeryResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDocumentProcessing, setIsDocumentProcessing] = useState(false);

  const safeJSONParse = (input: unknown): JSONValue | null => {
    try {
      if (typeof input === "string") {
        const parsed = JSON.parse(input);
        return isValidJSONValue(parsed) ? parsed : null;
      }
      return isValidJSONValue(input) ? input : null;
    } catch (error) {
      console.error("Error parsing JSON:", error);
      return null;
    }
  };

  const transformForgeryResult = useCallback(
    (apiResult: GetIdpFdQuery["idpFdDocument"]): ForgeryResult | null => {
      try {
        if (!apiResult) {
          return null;
        }

        const forgeryResult = apiResult;

        const forgeryDetails = forgeryResult?.fdResult?.forgery_details;
        const detailsArray = isJSONArray(forgeryDetails) ? forgeryDetails : [];

        const n8nDetails = forgeryResult?.n8nResult ?? [];

        const allDetails = [...detailsArray, ...n8nDetails];

        const categoryResults = new Map<
          ForgeryCheckType,
          {
            passed: boolean;
            verified: boolean;
            details: string[];
            subChecks: string[];
          }
        >();

        const allCategories = Object.values(ForgeryCheckType);

        allCategories.forEach((category) => {
          categoryResults.set(category, {
            passed: true,
            verified: false,
            details: [],
            subChecks: [],
          });
        });

        allDetails
          .filter((detail): detail is JSONObject => isJSONObject(detail))
          .forEach((detail: JSONObject) => {
            const method = String(detail.method || "Unknown Check");
            const category = getCategoryFromMethod(method);

            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const categoryResult = categoryResults.get(category)!;

            const result = detail.result as JSONObject;
            const isForged =
              result?.is_forged === true ||
              result?.forged === true ||
              String(result?.forged) === "true" ||
              String(result?.is_forged) === "true";

            categoryResult.verified = true;

            if (isForged) {
              categoryResult.passed = false;
            }

            const details = String(
              result?.reasons || result?.reason || detail.error_message || ""
            );

            if (details.trim()) {
              categoryResult.details.push(details);
            }

            categoryResult.subChecks.push(method);
          });

        const checks: ForgeryCheckResult[] = Array.from(
          categoryResults.entries()
        ).map(([category, result]) => ({
          type: category,
          passed: result.passed,
          verified: result.verified,
          details:
            result.details.join("; ") ||
            (result.verified
              ? "All checks in this category passed"
              : "No checks performed for this category"),
          subChecks: result.subChecks,
        }));

        const hasFailedChecks = checks.some(
          (check) => check.verified && !check.passed
        );
        const overallForged =
          Boolean(forgeryResult.fdResult.forged) || hasFailedChecks;

        return {
          status: overallForged ? ForgeryStatus.FORGED : ForgeryStatus.CLEAN,
          message: overallForged
            ? "Document has potential forgery indicators"
            : "Document appears to be authentic",
          checks,
        };
      } catch (error) {
        console.error("Error transforming forgery result:", error);
        return null;
      }
    },
    []
  );

  useEffect(() => {
    if (documentId && idpData?.idpFdDocument) {
      const doc = idpData.idpFdDocument;
      const fileNameFormatted = doc.document?.filePath?.substring(1) ?? "";

      const documentFromApi: UploadedDocument = {
        id: doc.id.toString(),
        type: mapDocTypeToDocumentType(doc.documentType),
        fileName: fileNameFormatted,
        fileSize: 0,
        fileUrl: doc.document?.url || "",
        filePath: doc.document?.filePath ?? "",
        uploadDate: new Date(doc.createdAt || Date.now()),
        companyName: doc.customer?.name || "Unknown Company",
        status:
          doc.status === Status.Completed
            ? DocumentStatus.PROCESSED
            : doc.status === Status.Failed
            ? DocumentStatus.FAILED
            : DocumentStatus.PENDING,
      };

      setCurrentDocument(documentFromApi);

      const isStillProcessing = doc.status !== Status.Completed;
      setIsDocumentProcessing(isStillProcessing);

      if (doc.idpResult) {
        const parsedData = safeJSONParse(doc.idpResult);
        setCurrentExtractedData(parsedData);
      }

      if (doc.fdResult || doc.n8nResult) {
        const transformedResult = transformForgeryResult(doc);
        setCurrentForgeryResult(
          transformedResult || {
            status: ForgeryStatus.CLEAN,
            message: "Forgery detection completed",
            checks: [],
          }
        );
      }

      setIsLoading(isStillProcessing);
    }
  }, [documentId, idpData, transformForgeryResult]);

  const handleSaveExtractedData = async (
    updatedData: JSONValue,
    documentType: DocumentType
  ) => {
    if (!documentId) {
      notifications.show({
        message: "No document ID available for saving",
        variant: "error",
      });
      return;
    }

    try {
      await updateIdpResult(Number(documentId), updatedData, documentType);
      setCurrentExtractedData(updatedData);
      notifications.show({
        message: "Document data updated successfully",
        variant: "success",
      });
    } catch (error) {
      console.error("Error saving extracted data:", error);
      notifications.show({
        message: "Failed to save document data. Please try again.",
        variant: "error",
      });
    }
  };

  const handleUploadClick = () => {
    setUploadModalOpened(true);
  };

  const handleCloseUploadModal = () => {
    setUploadModalOpened(false);
  };

  const handlePrepareSubmitDocument = (
    file: File,
    documentType: DocumentType,
    customerId: number
  ) => {
    setPendingUpload({ file, documentType, customerId });
    setUploadModalOpened(false);
    setConfirmationDialogOpened(true);
  };

  const handleNavigateToIDPDatabasePage = () => {
    navigate(`/idp-database`);
  };

  const handleConfirmUpload = async () => {
    if (pendingUpload) {
      const { file, documentType, customerId } = pendingUpload;

      try {
        const result = await processDocument(file, documentType, customerId);

        if (result.success && result.documentId) {
          navigate(`/idp-forgery-check?id=${result.documentId}`);
          notifications.show({
            message: "Document uploaded successfully",
            variant: "success",
          });
          setProcessingModalOpened(true);
        } else {
          notifications.show({
            message: result.error || "Failed to upload document",
            variant: "error",
          });
        }
      } catch (error) {
        notifications.show({
          message: "An error occurred while uploading the document",
          variant: "error",
        });
      }

      setPendingUpload(null);
    }
    setConfirmationDialogOpened(false);
  };

  const handleCancelUpload = () => {
    setPendingUpload(null);
    setConfirmationDialogOpened(false);
  };

  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (documentId && isDocumentProcessing && !uploadModalOpened) {
      intervalId = setInterval(() => {
        reexecuteIdpQuery();
      }, 10000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [documentId, isDocumentProcessing, reexecuteIdpQuery, uploadModalOpened]);

  const handleRefresh = () => {
    if (documentId) {
      reexecuteIdpQuery();
      notifications.show({
        message: "Document data refreshed",
        variant: "success",
      });
    }
  };

  const handleProcessingModalClose = () => {
    setProcessingModalOpened(false);

    reexecuteIdpQuery();
  };

  const handleDownloadCSV = () => {
    if (!currentExtractedData) return;

    const filename = `extracted_data_${currentDocument?.filePath?.substring(
      1
    )}`;

    track({
      input: {
        eventType: ActivityTrackerTypes.DownloadTrxRk,
        metadata: {
          documentId: documentId ? Number(documentId) : null,
          customerId: idpData?.idpFdDocument?.customer?.id || null,
        },
      },
    });

    currentDocument?.type === DocumentType.REKENING_KORAN
      ? downloadExcel(currentExtractedData, filename, currentForgeryResult)
      : downloadCSV(currentExtractedData, filename);
  };

  useEffect(() => {
    if (documentId && idpError) {
      notifications.show({
        title: "Error loading document",
        message: "Please try again later",
        variant: "error",
      });
      navigate(AppRoute.IDPDatabase);
    }
  }, [documentId, idpError, navigate]);

  if (documentId && fetchingIdpData) {
    return (
      <Layout>
        <Loader />
      </Layout>
    );
  }

  return (
    <Layout>
      <Flex direction="column" gap={{ base: 20, sm: 24 }}>
        <Flex direction="column" gap={20}>
          <Flex justify="space-between">
            <Title>
              Intelligent Document Processing (IDP) & Forgery Detection (FD)
            </Title>

            <Button
              uppercase
              variant="filledBlue"
              onClick={handleNavigateToIDPDatabasePage}
            >
              IDP Database
            </Button>
          </Flex>
          <Flex gap={16}>
            <Button
              leftIcon={<SvgUpload />}
              uppercase
              variant="filledGolden"
              onClick={handleUploadClick}
            >
              Upload Document
            </Button>
            {documentId && (
              <>
                <Button
                  variant="outlineBlue"
                  disabled={fetchingIdpData}
                  size="sm"
                  uppercase
                  onClick={handleRefresh}
                >
                  Refresh
                </Button>
                <Button
                  variant="outlineBlue"
                  leftIcon={<SvgDownload />}
                  disabled={!currentExtractedData || isDocumentProcessing}
                  size="sm"
                  uppercase
                  onClick={handleDownloadCSV}
                >
                  {currentDocument?.type === DocumentType.REKENING_KORAN
                    ? "Download Excel"
                    : "Download CSV"}
                </Button>
              </>
            )}
          </Flex>
        </Flex>

        <Flex gap={20}>
          <SimpleGrid cols={2} w="100%">
            <Flex h="70vh" direction="column" gap={16}>
              <Tabs w="100%" h="70vh" value="preview" variant="outline">
                <Tabs.List>
                  <Tabs.Tab value="preview">Uploaded Document Preview</Tabs.Tab>
                </Tabs.List>
                <Tabs.Panel value="preview" h="70vh">
                  <DocumentPreview document={currentDocument} />
                </Tabs.Panel>
              </Tabs>
            </Flex>
            <Flex mih="70vh" h="100%" direction="column" gap={16}>
              <Tabs
                w="100%"
                mih="70vh"
                h="fit-content"
                value={activeTab}
                variant="outline"
              >
                <Tabs.List>
                  <Tabs.Tab
                    value="extractedData"
                    onClick={() => setActiveTab("extractedData")}
                  >
                    <Flex align="center" gap={8}>
                      <Text>Extracted Data</Text>
                      {currentExtractedData && !isDocumentProcessing && (
                        <SvgCheckCircle
                          color={get(theme, "colors.green.0", "green")}
                        />
                      )}
                    </Flex>
                  </Tabs.Tab>
                  <Tabs.Tab
                    value="forgeryResult"
                    onClick={() => setActiveTab("forgeryResult")}
                  >
                    <Flex align="center" gap={8}>
                      <Text>Forgery Result</Text>
                      {currentForgeryResult &&
                        !isDocumentProcessing &&
                        (currentForgeryResult.status ===
                        ForgeryStatus.FORGED ? (
                          <SvgCloseCircle
                            color={get(theme, "colors.red.0", "red")}
                          />
                        ) : (
                          <SvgCheckCircle
                            color={get(theme, "colors.green.0", "green")}
                          />
                        ))}
                    </Flex>
                  </Tabs.Tab>
                </Tabs.List>
                <Tabs.Panel value="extractedData" h="70vh">
                  <ExtractedDataDisplay
                    data={currentExtractedData}
                    loading={isLoading || updateLoading || isDocumentProcessing}
                    documentType={currentDocument?.type}
                    documentId={documentId || undefined}
                    onSave={handleSaveExtractedData}
                    isProccesingFailed={
                      currentDocument?.status === DocumentStatus.FAILED
                    }
                  />
                </Tabs.Panel>
                <Tabs.Panel value="forgeryResult" h="70vh">
                  <ForgeryResultDisplay
                    result={currentForgeryResult}
                    loading={isLoading || isDocumentProcessing}
                    isProccesingFailed={
                      currentDocument?.status === DocumentStatus.FAILED
                    }
                  />
                </Tabs.Panel>
              </Tabs>
            </Flex>
          </SimpleGrid>
        </Flex>
      </Flex>

      <DocumentUploadModal
        opened={uploadModalOpened}
        onClose={handleCloseUploadModal}
        onSubmit={handlePrepareSubmitDocument}
      />
      {pendingUpload && (
        <DocumentUploadConfirmationDialog
          opened={confirmationDialogOpened}
          onClose={handleCancelUpload}
          onConfirm={handleConfirmUpload}
          documentType={pendingUpload.documentType}
          documentName={pendingUpload.file.name}
          loading={loadingSubmitDocument}
        />
      )}
      <ProcessingModal
        opened={processingModalOpened}
        onClose={handleProcessingModalClose}
        documentType={currentDocument?.type}
        companyName={currentDocument?.companyName}
        fileName={currentDocument?.fileName}
      />
    </Layout>
  );
};

export default IDPForgeryCheckPage;
